// Color utility system for consistent color usage across the application

// Color variant types
export type ColorVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'neutral';
export type ColorShade =
  | 50
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 950;
export type ColorIntensity =
  | 'lightest'
  | 'lighter'
  | 'light'
  | 'default'
  | 'dark'
  | 'darker'
  | 'darkest';

// Color mappings for consistent usage
export const colorVariants: Record<ColorVariant, string> = {
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'info',
  neutral: 'neutral',
};

// Semantic color mappings
export const semanticColors = {
  // Brand colors
  brand: {
    primary: 'primary',
    secondary: 'secondary',
  },

  // UI colors
  ui: {
    background: 'bg-primary',
    surface: 'bg-secondary',
    muted: 'bg-muted',
    disabled: 'bg-disabled',
  },

  // Text colors
  text: {
    primary: 'text-primary',
    secondary: 'text-secondary',
    muted: 'text-muted',
    disabled: 'text-disabled',
  },

  // Border colors
  border: {
    primary: 'border-primary',
    secondary: 'border-secondary',
    muted: 'border-muted',
  },

  // State colors
  state: {
    success: 'success',
    error: 'danger',
    warning: 'warning',
    info: 'info',
  },
};

// Color intensity mappings for different shades
export const colorIntensityMap: Record<ColorIntensity, ColorShade> = {
  lightest: 50,
  lighter: 100,
  light: 200,
  default: 500,
  dark: 700,
  darker: 800,
  darkest: 900,
};

// Utility functions for color class generation
export function getColorClass(
  variant: ColorVariant,
  intensity: ColorIntensity = 'default',
  prefix: 'bg' | 'text' | 'border' = 'bg',
): string {
  const shade = colorIntensityMap[intensity];
  const colorName = colorVariants[variant];

  if (intensity === 'default') {
    return `${prefix}-${colorName}`;
  }

  return `${prefix}-${colorName}-${shade}`;
}

// Specific utility functions for common use cases
export function getBgColor(
  variant: ColorVariant,
  intensity: ColorIntensity = 'default',
): string {
  return getColorClass(variant, intensity, 'bg');
}

export function getTextColor(
  variant: ColorVariant,
  intensity: ColorIntensity = 'default',
): string {
  return getColorClass(variant, intensity, 'text');
}

export function getBorderColor(
  variant: ColorVariant,
  intensity: ColorIntensity = 'default',
): string {
  return getColorClass(variant, intensity, 'border');
}

// Hover and focus state helpers
export function getHoverColor(
  variant: ColorVariant,
  intensity: ColorIntensity = 'dark',
): string {
  return `hover:${getBgColor(variant, intensity)}`;
}

export function getFocusColor(variant: ColorVariant): string {
  return `focus:ring-${colorVariants[variant]} focus:border-${colorVariants[variant]}`;
}

// Component-specific color utilities
export const buttonColors = {
  primary: {
    base: 'bg-primary-600 text-white',
    hover: 'hover:bg-primary-700',
    focus: 'focus:ring-primary-500',
    disabled: 'disabled:bg-primary-300',
  },
  secondary: {
    base: 'bg-secondary text-white',
    hover: 'hover:bg-secondary-600',
    focus: 'focus:ring-secondary-500',
    disabled: 'disabled:bg-secondary-300',
  },
  success: {
    base: 'bg-success text-white',
    hover: 'hover:bg-success-600',
    focus: 'focus:ring-success-500',
    disabled: 'disabled:bg-success-300',
  },
  danger: {
    base: 'bg-danger text-white',
    hover: 'hover:bg-danger-600',
    focus: 'focus:ring-danger-500',
    disabled: 'disabled:bg-danger-300',
  },
  warning: {
    base: 'bg-warning text-white',
    hover: 'hover:bg-warning-600',
    focus: 'focus:ring-warning-500',
    disabled: 'disabled:bg-warning-300',
  },
  info: {
    base: 'bg-info text-white',
    hover: 'hover:bg-info-600',
    focus: 'focus:ring-info-500',
    disabled: 'disabled:bg-info-300',
  },
  neutral: {
    base: 'bg-neutral text-white',
    hover: 'hover:bg-neutral-600',
    focus: 'focus:ring-neutral-500',
    disabled: 'disabled:bg-neutral-300',
  },
};

// Form element color utilities
export const formColors = {
  input: {
    base: 'bg-white border-border-primary text-text-primary',
    focus: 'focus:border-primary focus:ring-primary-500',
    error: 'border-danger focus:border-danger focus:ring-danger-500',
    disabled: 'bg-bg-disabled border-border-secondary text-text-disabled',
  },
  select: {
    base: 'bg-white border-border-primary text-text-primary',
    focus: 'focus:border-primary focus:ring-primary-500',
    error: 'border-danger focus:border-danger focus:ring-danger-500',
    disabled: 'bg-bg-disabled border-border-secondary text-text-disabled',
  },
  textarea: {
    base: 'bg-white border-border-primary text-text-primary',
    focus: 'focus:border-primary focus:ring-primary-500',
    error: 'border-danger focus:border-danger focus:ring-danger-500',
    disabled: 'bg-bg-disabled border-border-secondary text-text-disabled',
  },
};

// Card and container color utilities
export const containerColors = {
  card: {
    base: 'bg-white border-border-primary',
    hover: 'hover:bg-bg-secondary',
    dark: 'dark:bg-boxdark dark:border-strokedark',
  },
  panel: {
    base: 'bg-bg-secondary border-border-secondary',
    dark: 'dark:bg-boxdark-2 dark:border-strokedark',
  },
  modal: {
    base: 'bg-white border-border-primary',
    overlay: 'bg-black/50',
    dark: 'dark:bg-boxdark dark:border-strokedark',
  },
};

// Legacy color mappings for gradual migration
export const legacyColorMapping = {
  'meta-1': 'danger',
  'meta-2': 'primary-50',
  'meta-3': 'success-500',
  'meta-4': 'neutral-800',
  'meta-5': 'primary', // This was the duplicate
  'meta-6': 'primary-400', // This was the duplicate
  'meta-7': 'danger-400',
  'meta-8': 'warning-500',
  'meta-9': 'primary-100',
  'pink-500': 'primary',
  'pink-400': 'primary-400',
  'pink-600': 'primary-600',
  'pink-300': 'primary-300',
  'pink-700': 'primary-700',
};

// Helper function to migrate legacy colors
export function migrateLegacyColor(legacyColor: string): string {
  return (
    legacyColorMapping[legacyColor as keyof typeof legacyColorMapping] ||
    legacyColor
  );
}
